import React from 'react';
import { usePage, Link, router } from '@inertiajs/react';
import { Plus, Users, Mail, MessageCircle, MoreHorizontal } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import AppShell, { AppShellProps } from '@/components/AppShell';

interface Invitation {
  id: number;
  name: string;
  email_subject: string | null;
  job_status: string | null;
  invitation_type: string | null;
  communication_type: string | null;
  created_at: string;
  all_users_sent_successfully?: boolean;
  delivery_summary?: Record<string, number>;
}

interface PageProps extends AppShellProps {
  invitations: Invitation[];
  flash?: { alert?: string; notice?: string };
}

export default function InvitationsIndex() {
  const props = usePage<PageProps>().props;
  const { invitations, flash } = props;

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this invitation?')) {
        // Assume there's a destroy method
      router.delete(`/invitations/${id}`);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch(status) {
      case 'done':
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Completed</span>;
      case 'running':
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">Running</span>;
      case 'pending':
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      default:
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">{status || 'Draft'}</span>;
    }
  };

  return (
    <AppShell {...props}>
      <div className="max-w-screen-xl mx-auto py-10 px-4 md:px-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Invitations</h1>
          <p className="text-gray-600 mt-1">Manage event invitations, status, and reporting.</p>
        </div>
        <div className="flex space-x-3">
          <Link
            href="/invitations/new"
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded shadow-sm transition"
          >
            <Plus className="h-4 w-4" />
            Create Invitation
          </Link>
        </div>
      </div>

      {flash?.alert && (
        <div className="bg-red-50 text-red-600 p-4 rounded mb-6 border border-red-200">
          {flash.alert}
        </div>
      )}
      {flash?.notice && (
        <div className="bg-green-50 text-green-600 p-4 rounded mb-6 border border-green-200">
          {flash.notice}
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-100">
        <ul className="divide-y divide-gray-200">
          {invitations?.length > 0 ? (
            invitations.map((inv) => (
              <li key={inv.id} className="hover:bg-gray-50 transition-colors">
                <div className="px-4 py-4 flex items-center sm:px-6">
                  <div className="min-w-0 flex-1 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <Link 
                          href={`/invitations/${inv.id}/manage_users`} 
                          className="font-medium text-lg text-indigo-600 hover:text-indigo-500 truncate"
                        >
                          {inv.name || `Invitation #${inv.id}`}
                        </Link>
                        {getStatusBadge(inv.job_status)}
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                        {inv.communication_type && (
                          <div className="flex items-center gap-1">
                            {inv.communication_type.includes('email') ? <Mail className="h-3 w-3" /> : <MessageCircle className="h-3 w-3" />}
                            <span className="capitalize">{inv.communication_type}</span>
                          </div>
                        )}
                        {inv.delivery_summary && (
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            <span>{inv.delivery_summary.total || 0} Users</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {inv.delivery_summary && (
                        <div className="text-right text-sm">
                          <p className="text-green-600 font-medium">{inv.delivery_summary.delivered || 0} Delivered</p>
                          {(inv.delivery_summary.failed || 0) > 0 && <p className="text-red-500">{inv.delivery_summary.failed} Failed</p>}
                        </div>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 outline-none">
                          <MoreHorizontal className="h-5 w-5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem asChild>
                            <Link href={`/invitations/${inv.id}/manage_users`} className="w-full flex items-center cursor-pointer">
                              Manage Users
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href={`/invitations/${inv.id}/report`} className="w-full flex items-center cursor-pointer">
                              View Report
                            </Link>
                          </DropdownMenuItem>
                          {inv.job_status === 'pending' && (
                            <DropdownMenuItem asChild>
                              <Link href={`/invitations/${inv.id}/edit`} className="w-full flex items-center cursor-pointer">
                                Edit Setup
                              </Link>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDelete(inv.id)}
                            className="text-red-600 focus:text-red-700 cursor-pointer"
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <div className="px-4 py-12 text-center sm:px-6">
              <Mail className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invitations</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new invitation campaign.</p>
              <div className="mt-6">
                <Link
                  href="/invitations/new"
                  className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                >
                  <Plus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  New Invitation
                </Link>
              </div>
            </div>
          )}
        </ul>
      </div>
    </div>
    </AppShell>
  );
}
